"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { RiPhoneFill } from "react-icons/ri";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Tooltip from "@/components/ui/tooltip";
import { formatPhoneNumber } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { patientSidebarDefinition } from "@/features/sidebar/patient-sidebar";
import { type DeskPhoneScreenPop, useDeskPhoneScreenPopStore } from "@/stores/desk-phone-screen-pop-store";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
function patientLabel(pop: DeskPhoneScreenPop): string {
  const {
    firstName,
    lastName,
    preferredName
  } = pop.patient;
  const display = preferredName || firstName || "";
  const last = lastName || "";
  return `${display} ${last}`.trim() || "Unknown caller";
}

/**
 * Header indicator that lights up while at least one desk-phone call is
 * active. With one call, clicking opens the patient sidebar directly —
 * same surface the in-platform softphone uses post-call. With multiple
 * concurrent calls, clicking opens a small list so the user can pick
 * which call's patient to surface, instead of guessing "most recent".
 */
export function DeskPhoneCallIndicator() {
  const $ = _c(44);
  const pops = useDeskPhoneScreenPopStore(_temp);
  const remove = useDeskPhoneScreenPopStore(_temp2);
  const openSidebar = useRightSidebarStore(_temp3);
  const [popoverOpen, setPopoverOpen] = useState(false);
  let T0;
  let T1;
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== openSidebar || $[1] !== popoverOpen || $[2] !== pops || $[3] !== remove) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const entries = Object.values(pops).sort(_temp4);
      if (entries.length === 0) {
        t8 = null;
        break bb0;
      }
      let t9;
      if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = cx("relative mr-1 hidden h-8 items-center justify-center rounded-lg px-2 transition-colors md:flex", "text-status-success-text hover:bg-status-success-muted");
        $[15] = t9;
      } else {
        t9 = $[15];
      }
      const buttonClassName = t9;
      const renderIcon = _temp5;
      let t10;
      if ($[16] !== openSidebar || $[17] !== remove) {
        t10 = pop => {
          openSidebar(patientSidebarDefinition, {
            patientTiny: pop.patient
          });
          remove(pop.callId);
        };
        $[16] = openSidebar;
        $[17] = remove;
        $[18] = t10;
      } else {
        t10 = $[18];
      }
      const open = t10;
      if (entries.length === 1) {
        const pop_0 = entries[0];
        let t11;
        if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = renderIcon();
          $[19] = t11;
        } else {
          t11 = $[19];
        }
        t8 = <Tooltip content={`${patientLabel(pop_0)} — open patient`} side="bottom" triggerAsChild={true}><button type="button" onClick={() => open(pop_0)} aria-label={`Active desk-phone call from ${patientLabel(pop_0)}`} className={buttonClassName}>{t11}</button></Tooltip>;
        break bb0;
      }
      T1 = Popover;
      t5 = popoverOpen;
      t6 = setPopoverOpen;
      const t11 = `${entries.length} active desk-phone calls`;
      let t12;
      if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = renderIcon();
        $[20] = t12;
      } else {
        t12 = $[20];
      }
      const t13 = <button type="button" aria-label={`${entries.length} active desk-phone calls`} className={buttonClassName}>{t12}<span className="ml-1.5 text-caption-strong tabular-nums">{entries.length}</span></button>;
      let t14;
      if ($[21] !== t13) {
        t14 = <PopoverTrigger asChild={true}>{t13}</PopoverTrigger>;
        $[21] = t13;
        $[22] = t14;
      } else {
        t14 = $[22];
      }
      if ($[23] !== t11 || $[24] !== t14) {
        t7 = <Tooltip content={t11} side="bottom" triggerAsChild={true}>{t14}</Tooltip>;
        $[23] = t11;
        $[24] = t14;
        $[25] = t7;
      } else {
        t7 = $[25];
      }
      T0 = PopoverContent;
      t2 = "end";
      t3 = "w-72 p-1";
      if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <div className="px-2 pb-1 pt-1.5 text-overline text-foreground-secondary">Active desk-phone calls</div>;
        $[26] = t4;
      } else {
        t4 = $[26];
      }
      t0 = "flex flex-col";
      let t15;
      if ($[27] !== open) {
        t15 = pop_1 => <li key={pop_1.callId}><button type="button" onClick={() => {
            open(pop_1);
            setPopoverOpen(false);
          }} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-hover"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-status-success-muted text-status-success-text"><RiPhoneFill className="h-3.5 w-3.5" /></span><span className="flex min-w-0 flex-col"><span className="truncate text-body-strong text-foreground">{patientLabel(pop_1)}</span><span className="truncate text-caption text-foreground-secondary">{pop_1.patient.phoneNumber ? formatPhoneNumber(pop_1.patient.phoneNumber) || pop_1.patient.phoneNumber : pop_1.direction === "INBOUND" ? "Incoming" : "Outgoing"}</span></span></button></li>;
        $[27] = open;
        $[28] = t15;
      } else {
        t15 = $[28];
      }
      t1 = entries.map(t15);
    }
    $[0] = openSidebar;
    $[1] = popoverOpen;
    $[2] = pops;
    $[3] = remove;
    $[4] = T0;
    $[5] = T1;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    T0 = $[4];
    T1 = $[5];
    t0 = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  let t9;
  if ($[29] !== t0 || $[30] !== t1) {
    t9 = <ul className={t0}>{t1}</ul>;
    $[29] = t0;
    $[30] = t1;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== T0 || $[33] !== t2 || $[34] !== t3 || $[35] !== t4 || $[36] !== t9) {
    t10 = <T0 align={t2} className={t3}>{t4}{t9}</T0>;
    $[32] = T0;
    $[33] = t2;
    $[34] = t3;
    $[35] = t4;
    $[36] = t9;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== T1 || $[39] !== t10 || $[40] !== t5 || $[41] !== t6 || $[42] !== t7) {
    t11 = <T1 open={t5} onOpenChange={t6}>{t7}{t10}</T1>;
    $[38] = T1;
    $[39] = t10;
    $[40] = t5;
    $[41] = t6;
    $[42] = t7;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  return t11;
}
function _temp5() {
  return <span className="relative flex"><RiPhoneFill className="h-4 w-4" /><span aria-hidden={true} className="absolute -right-0.5 -top-0.5 flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-status-success-text opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-status-success-text" /></span></span>;
}
function _temp4(a, b) {
  return b.receivedAt - a.receivedAt;
}
function _temp3(state) {
  return state.open;
}
function _temp2(s_0) {
  return s_0.remove;
}
function _temp(s) {
  return s.pops;
}